import { useEffect, useState } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import { 
  Package, ArrowLeft, Download, Truck, CheckCircle, Clock,
  MapPin, Phone, Mail, RotateCcw
} from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export const AccountOrderDetail = () => {
  const { orderId } = useParams();
  const navigate = useNavigate();
  const [order, setOrder] = useState(null);
  const [timeline, setTimeline] = useState([]);
  const [loading, setLoading] = useState(true);
  const [downloading, setDownloading] = useState(false);

  useEffect(() => {
    loadOrderDetail();
  }, [orderId]);

  const loadOrderDetail = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/account/orders/${orderId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setOrder(response.data.order);
      setTimeline(response.data.timeline || []);
    } catch (error) {
      console.error('Error loading order:', error);
      toast.error('Order not found');
      navigate('/account/orders');
    } finally {
      setLoading(false);
    }
  };

  const downloadInvoice = async () => {
    setDownloading(true);
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/account/orders/${orderId}/invoice`, {
        headers: { Authorization: `Bearer ${token}` },
        responseType: 'blob'
      });
      
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `invoice_${order?.order_number}.pdf`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      
      toast.success('Invoice downloaded');
    } catch (error) {
      toast.error('Failed to download invoice');
    } finally {
      setDownloading(false);
    }
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-IN', {
      style: 'currency',
      currency: 'INR',
      maximumFractionDigits: 0
    }).format(amount || 0);
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    return new Date(dateString).toLocaleDateString('en-IN', {
      day: 'numeric',
      month: 'short',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const getStatusColor = (status) => {
    const colors = {
      pending: 'bg-yellow-100 text-yellow-800',
      confirmed: 'bg-blue-100 text-blue-800',
      processing: 'bg-indigo-100 text-indigo-800',
      packed: 'bg-purple-100 text-purple-800',
      shipped: 'bg-cyan-100 text-cyan-800',
      out_for_delivery: 'bg-orange-100 text-orange-800',
      delivered: 'bg-green-100 text-green-800',
      cancelled: 'bg-red-100 text-red-800',
      returned: 'bg-gray-100 text-gray-800'
    };
    return colors[status] || 'bg-gray-100 text-gray-800';
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <div className="w-8 h-8 border-2 border-gold border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  if (!order) {
    return null;
  }

  const canRequestReturn = order.order_status === 'delivered';

  return (
    <div className="space-y-6" data-testid="account-order-detail">
      {/* Header */}
      <div className="flex items-center justify-between flex-wrap gap-4">
        <div className="flex items-center gap-4">
          <Link
            to="/account/orders"
            className="p-2 hover:bg-beige transition-colors"
          >
            <ArrowLeft className="w-5 h-5 text-charcoal" />
          </Link>
          <div>
            <h1 className="font-heading text-2xl font-light text-charcoal">
              Order #{order.order_number}
            </h1>
            <p className="text-sm text-charcoal-light">{formatDate(order.created_at)}</p>
          </div>
        </div>
        
        <div className="flex items-center gap-3">
          {canRequestReturn && (
            <Link
              to={`/account/returns?order=${order.id}`}
              className="flex items-center gap-2 px-4 py-2 border border-charcoal/20 text-charcoal text-sm hover:bg-beige transition-colors"
            >
              <RotateCcw className="w-4 h-4" />
              Request Return
            </Link>
          )}
          <button
            onClick={downloadInvoice}
            disabled={downloading}
            className="flex items-center gap-2 px-4 py-2 bg-gold text-white text-sm hover:bg-gold-light disabled:opacity-50 transition-colors"
          >
            <Download className="w-4 h-4" />
            {downloading ? 'Downloading...' : 'Invoice'}
          </button>
        </div>
      </div>

      {/* Order Status */}
      <div className="bg-white border border-charcoal/10 p-6">
        <div className="flex items-center justify-between mb-6">
          <h2 className="font-heading text-lg font-light text-charcoal">Order Status</h2>
          <span className={`px-3 py-1 text-sm font-medium ${getStatusColor(order.order_status)}`}>
            {order.order_status?.replace('_', ' ').toUpperCase()}
          </span>
        </div>

        {/* Timeline */}
        <div className="relative">
          <div className="absolute left-4 top-0 bottom-0 w-0.5 bg-charcoal/10" />
          
          {timeline.map((step, index) => (
            <div key={step.status} className="relative flex items-start gap-4 pb-6 last:pb-0">
              <div className={`w-8 h-8 rounded-full flex items-center justify-center flex-shrink-0 z-10 ${
                step.completed ? 'bg-gold text-white' : 'bg-white border-2 border-charcoal/20 text-charcoal-light'
              }`}>
                {step.completed ? (
                  <CheckCircle className="w-5 h-5" />
                ) : (
                  <Clock className="w-4 h-4" />
                )}
              </div>
              <div className="flex-1 pt-1">
                <p className={`font-medium ${step.completed ? 'text-charcoal' : 'text-charcoal-light'}`}>
                  {step.label}
                </p>
                {step.completed && step.date && (
                  <p className="text-xs text-charcoal-light">{formatDate(step.date)}</p>
                )}
              </div>
            </div>
          ))}
        </div>

        {/* Tracking Info */}
        {order.tracking_number && (
          <div className="mt-6 p-4 bg-beige/50 flex items-center gap-4">
            <Truck className="w-5 h-5 text-gold" />
            <div>
              <p className="text-sm text-charcoal">Tracking Number: <strong>{order.tracking_number}</strong></p>
              {order.tracking_url && (
                <a href={order.tracking_url} target="_blank" rel="noopener noreferrer" className="text-xs text-gold hover:underline">
                  Track your shipment
                </a>
              )}
            </div>
          </div>
        )}
      </div>

      {/* Order Items */}
      <div className="bg-white border border-charcoal/10">
        <div className="p-4 border-b border-charcoal/10">
          <h2 className="font-heading text-lg font-light text-charcoal">Order Items</h2>
        </div>
        <div className="divide-y divide-charcoal/5">
          {order.items?.map((item, idx) => (
            <div key={idx} className="p-4 flex items-center gap-4">
              <div className="w-20 h-20 bg-beige flex-shrink-0">
                {item.image ? (
                  <img src={item.image} alt={item.product_name} className="w-full h-full object-cover" />
                ) : (
                  <div className="w-full h-full flex items-center justify-center">
                    <Package className="w-8 h-8 text-charcoal-light" />
                  </div>
                )}
              </div>
              <div className="flex-1">
                <p className="font-medium text-charcoal">{item.product_name}</p>
                <p className="text-sm text-charcoal-light">
                  Size: {item.size} | Color: {item.color || 'Standard'}
                </p>
                <p className="text-sm text-charcoal-light">Qty: {item.quantity}</p>
              </div>
              <div className="text-right">
                <p className="font-medium text-charcoal">{formatCurrency(item.price * item.quantity)}</p>
                <p className="text-xs text-charcoal-light">{formatCurrency(item.price)} each</p>
              </div>
            </div>
          ))}
        </div>
        
        {/* Order Summary */}
        <div className="p-4 border-t border-charcoal/10 bg-beige/30">
          <div className="flex justify-between text-sm mb-2">
            <span className="text-charcoal-light">Subtotal</span>
            <span className="text-charcoal">
              {formatCurrency(order.items?.reduce((sum, item) => sum + (item.price * item.quantity), 0))}
            </span>
          </div>
          <div className="flex justify-between text-sm mb-2">
            <span className="text-charcoal-light">Shipping</span>
            <span className="text-charcoal">
              {order.total_amount >= 999 ? 'FREE' : formatCurrency(99)}
            </span>
          </div>
          <div className="flex justify-between font-medium text-lg pt-2 border-t border-charcoal/10">
            <span className="text-charcoal">Total</span>
            <span className="text-gold">{formatCurrency(order.total_amount)}</span>
          </div>
        </div>
      </div>

      {/* Shipping & Payment Info */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        {/* Shipping Address */}
        <div className="bg-white border border-charcoal/10 p-6">
          <h2 className="font-heading text-lg font-light text-charcoal mb-4 flex items-center gap-2">
            <MapPin className="w-5 h-5 text-gold" />
            Shipping Address
          </h2>
          <p className="text-charcoal font-medium">{order.customer_name}</p>
          <p className="text-charcoal-light whitespace-pre-line mt-2">{order.shipping_address}</p>
          <div className="mt-4 space-y-1 text-sm text-charcoal-light">
            <p className="flex items-center gap-2">
              <Phone className="w-4 h-4" /> {order.customer_phone}
            </p>
            <p className="flex items-center gap-2">
              <Mail className="w-4 h-4" /> {order.customer_email}
            </p>
          </div>
        </div>

        {/* Payment Info */}
        <div className="bg-white border border-charcoal/10 p-6">
          <h2 className="font-heading text-lg font-light text-charcoal mb-4">Payment Information</h2>
          <div className="space-y-3">
            <div className="flex justify-between">
              <span className="text-charcoal-light">Payment Method</span>
              <span className="text-charcoal font-medium">{order.payment_method?.toUpperCase()}</span>
            </div>
            <div className="flex justify-between">
              <span className="text-charcoal-light">Payment Status</span>
              <span className={`px-2 py-0.5 text-xs ${
                order.payment_status === 'completed' ? 'bg-green-100 text-green-800' : 'bg-yellow-100 text-yellow-800'
              }`}>
                {order.payment_status?.toUpperCase()}
              </span>
            </div>
            {order.razorpay_payment_id && (
              <div className="flex justify-between">
                <span className="text-charcoal-light">Transaction ID</span>
                <span className="text-charcoal text-sm">{order.razorpay_payment_id}</span>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Need Help */}
      <div className="bg-beige/50 p-6 text-center">
        <h3 className="font-heading text-lg font-light text-charcoal mb-2">Need Help?</h3>
        <p className="text-sm text-charcoal-light mb-4">
          Contact our support team for any questions about your order.
        </p>
        <a
          href="mailto:support@jatfashion.com"
          className="inline-flex items-center gap-2 text-gold hover:underline"
        >
          <Mail className="w-4 h-4" />
          support@jatfashion.com
        </a>
      </div>
    </div>
  );
};

export default AccountOrderDetail;
